import { Badge, Flex } from '@chakra-ui/react';
import { Text } from '@chakra-ui/react';
import { IAuthorICMJEForm, IRoleType } from '@publishing-engine/domain/models';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import dateFormat from 'dateformat';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  fetchICMJEForm,
  requestSubmittingAuthorOnlyICMJEForm
} from '../../../api/queries';
import { OutlineCard } from '../MySubmission/PreferencesTab';
import { useSubmissionFields } from '../../../hooks/useSubmissionFields';
import { FormFieldSubmittingAuthor } from '@publishing-engine/domain/types/article';
import { useState } from 'react';
import { EditorialRequestICMJEFormModal } from '../ICMJEFormSubmittingAuthorOnly/EditorialRequestICMJEForm';
import { ButtonText } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faEdit,
  faInfoCircle,
  faUpload
} from '@fortawesome/pro-regular-svg-icons';
import ProtectedWorkflowAction from '../../ProtectedAction/ProtectedWorkflowAction';
import { downloadFile } from '../../../utils/file';
import { useToast } from '@morressier/alembic';
import { EditorialUploadICMJEFormModal } from '../ICMJEFormSubmittingAuthorOnly/EditorialUploadICMJEForm';
import { OverwriteICMJEFormModal } from '../ICMJEFormSubmittingAuthorOnly/OverwriteICMJEModal';

const ICMJEStatusDescription = ({
  isLoading,
  icmjeForm
}: {
  isLoading: boolean;
  icmjeForm?: IAuthorICMJEForm;
}) => {
  const { availableActions } = useSubmissionData();

  if (isLoading) {
    return (
      <Text textStyle="textBody" color="gray.500">
        Loading latest status...
      </Text>
    );
  }

  if (icmjeForm?.isRequested) {
    return (
      <Text>
        A form was requested – the Author will be informed to take action via
        email.
      </Text>
    );
  }

  if (icmjeForm?.file.url) {
    const submittedICMJEDescription = icmjeForm?.editorUpdated
      ? 'A form was uploaded by the Editors'
      : 'The author has provided a form';
    return (
      <Text>
        {submittedICMJEDescription} on{' '}
        {dateFormat(icmjeForm.date, 'mmmm d, yyyy')}.
      </Text>
    );
  }

  if (availableActions.contains('EditorialRequestCopyright')) {
    return (
      <Text textStyle="textBody">
        No Forms and Disclosures has been provided – request the Author to
        provide the form to proceed with publication.
      </Text>
    );
  }

  return (
    <>
      <Text textStyle="textBody">
        Forms and Disclosures becomes available for authors in the revision
        stage.
      </Text>
      <Text textStyle="textBody">
        If the authors have not provided this information by the time the
        submission enters pre-production, you will be able to request the form
        or request additional edits.
      </Text>
    </>
  );
};

const ICMJEDisclosureSubmittingAuthorOnly: React.FC = () => {
  const {
    availableActions,
    submissionId,
    submissionContextId,
    organizationId
  } = useSubmissionData();
  const icmjeFormQueryKey = [
    'submitting-author-only-icmje-form-editorial',
    { submissionId, roleType: 'editorial' }
  ];

  const [isRequestFormModalEnabled, setIsRequestFormModalEnabled] =
    useState<boolean>(false);
  const [isUploadICMJEModalEnabled, setIsUploadICMJEModalEnabled] =
    useState<boolean>(false);
  const [
    isOverwriteAgreementModalEnabled,
    setIsOverwriteAgreementModalEnabled
  ] = useState<'upload' | ''>('');

  const toast = useToast();

  const { getFieldState } = useSubmissionFields();
  const submittingAuthorField =
    getFieldState<FormFieldSubmittingAuthor>('SubmittingAuthor')!;

  const displayManualUploadButton = availableActions.contains(
    'EditorialUploadCopyright'
  );
  const displayRequestFormButton = availableActions.contains(
    'EditorialRequestCopyright'
  );

  const {
    data: submittingAuthorICMJE,
    isLoading,
    isFetching,
    refetch
  } = useQuery({
    queryKey: icmjeFormQueryKey,
    queryFn: () => fetchICMJEForm(submissionId, 'editorial' as IRoleType)
  });

  const icmjeForm = submittingAuthorICMJE?.authorICMJEForm.find(
    icmje => icmje.isSubmittingAuthor === true
  );

  const downloadFormButton = !!icmjeForm?.file?.url;

  const displayButtons =
    displayRequestFormButton || displayManualUploadButton || downloadFormButton;

  const fetchSubmissionAuthorOnlyICMJEFormMutation = useMutation({
    mutationFn: ({
      submissionId,
      email
    }: {
      submissionId: string;
      email: string;
    }) =>
      requestSubmittingAuthorOnlyICMJEForm(submissionId, {
        roleContext: 'editorial',
        type: 'request',
        email
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Request sent successfully',
        description: '',
        isClosable: true
      });
      refetch();
      setIsRequestFormModalEnabled(false);
    }
  });

  const sendEmailRequest = async () => {
    await fetchSubmissionAuthorOnlyICMJEFormMutation.mutateAsync({
      submissionId,
      email: submittingAuthorField.value
    });
  };

  const handleRequestForm = () => {
    if (icmjeForm?.file && icmjeForm.file.url) {
      setIsRequestFormModalEnabled(true);
    } else {
      sendEmailRequest();
    }
  };

  const [isFileDownloading, setIsFileDownloading] = useState(false);

  const handleFileDownload = async (fileUrl: string) => {
    try {
      setIsFileDownloading(true); // disable button
      await downloadFile(fileUrl);
    } finally {
      setIsFileDownloading(false); // re-enable no matter what
    }
  };

  return (
    <OutlineCard>
      {isRequestFormModalEnabled && (
        <EditorialRequestICMJEFormModal
          isPending={fetchSubmissionAuthorOnlyICMJEFormMutation.isPending}
          onSubmit={sendEmailRequest}
          onClose={() => setIsRequestFormModalEnabled(false)}
        />
      )}
      {isOverwriteAgreementModalEnabled && (
        <OverwriteICMJEFormModal
          onClose={() => setIsOverwriteAgreementModalEnabled('')}
          onSubmit={() => {
            if (isOverwriteAgreementModalEnabled === 'upload') {
              setIsUploadICMJEModalEnabled(true);
            }

            setIsOverwriteAgreementModalEnabled('');
          }}
        />
      )}
      {isUploadICMJEModalEnabled && (
        <EditorialUploadICMJEFormModal
          onClose={() => setIsUploadICMJEModalEnabled(false)}
          submissionId={submissionId}
          organizationId={organizationId}
          submissionContextId={submissionContextId}
          authorEmail={submittingAuthorField.value}
        />
      )}
      <Flex flexDirection="column" gap={4}>
        <Flex flexDirection="column" gap={2}>
          {icmjeForm?.isRequested && (
            <Flex justifyContent="space-between" alignItems="center">
              <Badge size="context" variant="attention">
                <FontAwesomeIcon icon={faInfoCircle} /> Changes requested
              </Badge>
            </Flex>
          )}
          <ICMJEStatusDescription
            isLoading={isLoading || isFetching}
            icmjeForm={icmjeForm}
          />
        </Flex>
        {displayButtons && (
          <Flex gap={6} flexWrap="wrap" alignItems="center">
            {downloadFormButton && (
              <Flex justifyContent="flex-start">
                <ButtonText
                  variant="secondary"
                  onClick={() => {
                    icmjeForm.file?.url &&
                      handleFileDownload(icmjeForm.file.url);
                  }}
                  isDisabled={isFileDownloading}
                  leftIcon={<FontAwesomeIcon icon={faDownload} />}
                >
                  Download Form
                </ButtonText>
              </Flex>
            )}
            {displayRequestFormButton && (
              <ProtectedWorkflowAction actionName="EditorialRequestCopyright">
                <ButtonText
                  onClick={() => handleRequestForm()}
                  disabled={
                    fetchSubmissionAuthorOnlyICMJEFormMutation.isPending
                  }
                  isLoading={
                    fetchSubmissionAuthorOnlyICMJEFormMutation.isPending
                  }
                  iconLeft={<FontAwesomeIcon icon={faEdit} />}
                >
                  Request Form
                </ButtonText>
              </ProtectedWorkflowAction>
            )}
            {displayManualUploadButton && (
              <ProtectedWorkflowAction actionName="EditorialUploadICMJE">
                <ButtonText
                  disabled={false}
                  iconLeft={<FontAwesomeIcon icon={faUpload} />}
                  onClick={() => {
                    if (icmjeForm?.file && icmjeForm?.file.url) {
                      setIsOverwriteAgreementModalEnabled('upload');
                    } else {
                      setIsUploadICMJEModalEnabled(true);
                    }
                  }}
                >
                  Upload Form Manually
                </ButtonText>
              </ProtectedWorkflowAction>
            )}
          </Flex>
        )}
      </Flex>
    </OutlineCard>
  );
};

export default ICMJEDisclosureSubmittingAuthorOnly;
